import React, { useMemo } from 'react';
import type { PopoverProps } from '@mui/material/Popover';
import Slide, { type SlideProps } from '@mui/material/Slide';
import cn from 'classnames';

import { useNotificationsCtx } from '../../model';

import PseudoPopover from './pseudo-popover';
import { useDragHandling } from './use-drag-handling';
import { usePopoverPosition } from './use-popover-position';
import classes from './notifications-drawer-popover.module.scss';
import type { DragDirection } from './types';

type NotificationsDrawerPopoverProps = {
  className?: string;
  direction: DragDirection;
  isUrgent?: boolean;
} & Pick<SlideProps, 'container'> &
  Pick<PopoverProps, 'anchorEl'>;

const mapDragDirectionToSlideDirection: Record<DragDirection, SlideProps['direction']> = {
  vertical: 'up',
  horizontal: 'right'
};

const NotificationsDrawerPopover = (props: React.PropsWithChildren<NotificationsDrawerPopoverProps>) => {
  const { direction, children, className, isUrgent = false } = props;
  const {
    drawer: { isOpen, anchorEl },
    closeNotifications
  } = useNotificationsCtx();
  const open = isOpen;

  const handleClose = () => {
    closeNotifications();
  };

  const { dragState, popoverRef, handlePointerDown } = useDragHandling({
    onClose: handleClose,
    direction
  });

  const { anchorOrigin, transformOrigin } = usePopoverPosition(direction);

  const paperStyle = useMemo<React.CSSProperties>(
    () => ({
      padding: 0,
      boxShadow: 'none',
      ...(dragState.isDragging && { userSelect: 'none' }),
      zIndex: direction === 'horizontal' ? 100 : 101,
      ...(direction === 'horizontal' && {
        borderRadius: '0px 5px 5px 0px',
        transform: `translateX(${dragState.transformX}px)`
      }),
      ...(direction === 'vertical' && {
        borderRadius: '5px 5px 0px 0px',
        transform: `translateY(${dragState.transformY}px)`
      }),
      ...(isUrgent && {
        background: 'transparent'
      })
    }),
    [direction, dragState.transformY, dragState.transformX, dragState.isDragging, isUrgent]
  );

  const DragHandle = useMemo(() => {
    if (isUrgent) {
      return null;
    }

    return direction === 'vertical' ? (
      <div className={classes['drag-handle']} onPointerDown={handlePointerDown} />
    ) : (
      <div className={classes['right-drag-handle']} onPointerDown={handlePointerDown} />
    );
  }, [direction, handlePointerDown, isUrgent]);

  return (
    <PseudoPopover
      anchorEl={anchorEl}
      open={open}
      onClose={() => closeNotifications()}
      TransitionComponent={Slide}
      // TODO: Remove this once the appropriate MUI version is installed
      // @ts-expect-error The current version of MUI does not inherit the props from the given Transition component
      TransitionProps={{ direction: mapDragDirectionToSlideDirection[direction] }}
      anchorOrigin={anchorOrigin}
      marginThreshold={0}
      transformOrigin={transformOrigin}
      PaperProps={{ className, style: paperStyle }}
    >
      <div
        ref={popoverRef}
        className={cn(classes['notifications-container'], classes[`notifications-container--${direction}`])}
      >
        {DragHandle}
        {children}
      </div>
    </PseudoPopover>
  );
};

export default NotificationsDrawerPopover;
